import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { FormArray, FormBuilder,  FormControl,  UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, Observable, Subject, catchError, combineLatest, concatMap, exhaustMap, filter, forkJoin, from, map, mergeMap, of, skip, startWith, switchMap, tap, withLatestFrom, } from 'rxjs';
import { AppStateInterface } from 'src/app/stores/appState.interface';
import * as InstallationActions from "../../stores/global/installations/installation-actions";
import * as CompanyActions from "../../stores/global/companies/company-actions";
import * as EmployeeActions from "../../stores/global/employees/employee-actions";
import { companySelector } from 'src/app/stores/global/companies/company-selectors';
import { UntilDestroy } from '@ngneat/until-destroy';
import { currentEmployeeSelector } from 'src/app/stores/global/current-employee/current-employee-selector';
import { ICreateInstallationRequest, IGetInstallationDetailResponse, IUpdateInstallationRequest } from '../models/installation';
import { InstallationStore } from 'src/app/stores/component/installation-store';
import { DeviceStore } from 'src/app/stores/component/device-store';
import { PermissionStorageService } from 'src/app/authentication/storage/permission-storage.service';
import { FileRestrictions, UploadEvent } from '@progress/kendo-angular-upload';
import { AttachmentStore } from 'src/app/stores/component/attachment-store';
import { AttachmentService } from 'src/app/shared/services/attachment.service';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { CompanyService } from 'src/app/company/services/company.service';
import { downloadBlob } from 'src/app/shared/helpers/fileHelper';
import { IBaseGetAvailableDevicesForInstallationRequest, IBaseGetByComanyRequest, IBaseSearchRequest, IFilter, IGridState, PageSize } from 'src/app/core/models/base';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { DeviceService } from 'src/app/devices/services/device.service';
import { IDeviceDetailsResponse, IDeviceMinDto } from 'src/app/devices/models/device';
import { resizeImageTo1920 } from 'src/app/shared/helpers/image-scaling';

@UntilDestroy()
@Component({
    selector: 'app-installation-detail',
    templateUrl: './installation-detail.component.html',
    styleUrl: './installation-detail.component.scss',
    providers: [InstallationStore, DeviceStore, AttachmentStore],
    standalone: false
})
export class InstallationDetailComponent {

  private originalFormValues: any;
  public deletionDialogOpened = false;

  isNew = false;
  isAdmin = false;
  showReset = false;
  installationId: string;
  currentUserId: string;
  currentCompanyId: string;
  currentTenant: string;
  reloadResults : boolean = true;
  loading: boolean = false;

  // Upload file type restrictions
  restrictions: FileRestrictions = {
    allowedExtensions: [".jpg", ".jpeg", ".png"],
  };

  // Create filter form
  filterForm = this.fb.group({
    keyword: new FormControl(null),
  });

  private tempLinkedDevices: IDeviceMinDto[] = []; // Array to hold temporarily linked devices


  // Initialize installationForm before it is used anywhere
  public installationForm: UntypedFormGroup;

  constructor(
    private store: Store<AppStateInterface>,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    private installationStore: InstallationStore,
    private deviceStore: DeviceStore,
    private deviceService: DeviceService,
    private permissionServie: PermissionStorageService,
    private attachmentStore: AttachmentStore,
    private attachmentService: AttachmentService,
    private companyService: CompanyService,
    private sanitizer: DomSanitizer,
    private router: Router,
    private cdRef: ChangeDetectorRef
  ) { 

  }

  ///////////////
  // Getters  //
  /////////////

  getControl(path: string) {
    return this.filterForm.get(path) as FormControl;
  }

  get pageSizes() {
    return PageSize;
  }

  ////////////////////////
  // (Behavior)Subjects //
  ///////////////////////

  searchRequest$ = new BehaviorSubject<IBaseGetAvailableDevicesForInstallationRequest>({
    pageNumber: 0,
    pageSize: PageSize.Small,
    companyId: null,
    installationId: null,
    linkedDeviceIds: []
  });

  // private addedDeviceIds$ = new BehaviorSubject<Set<string>>(new Set());
  // private removedDeviceIds$ = new BehaviorSubject<Set<IDeviceDetailsResponse>>(new Set());

  gridState$ = new BehaviorSubject<IGridState>({
    pageSize: PageSize.Small,
    skip: 0,
  });

  pageChangeSub$ = new Subject<PageChangeEvent>();

  vm$ = combineLatest([
    this.gridState$.pipe(),
    this.installationStore.selectedInstallation$.pipe(
      tap(installation => {
        // Perform side-effects when installation changes
        if (installation) {
          this.currentCompanyId = installation.companyId;
          this.currentTenant = installation.tenantId;
          this.patchFormValues(installation);

          // Patch searchRequest$
          this.searchRequest$.next({
            ...this.searchRequest$.getValue(),
            companyId: installation.companyId,
            installationId: installation.id,
            linkedDeviceIds: installation.devices.map(x => x.id)
          });

          this.originalFormValues = this.installationForm.value;
        }
      }),
    ),
    this.attachmentStore.images$.pipe(
      mergeMap(images => {
        if (!images || images.length === 0) {
          return of([]);
        }

        return forkJoin(
          images.map(image =>
            from(this.attachmentService.waitForThumbnail(image.id)).pipe(
              switchMap(hasThumbnail => {
                // if thumbnail exists -> download midsize
                if (hasThumbnail) {
                  return this.attachmentService.downloadMidsize({ attachmentId: image.id });
                }
                // otherwise -> download original
                return this.attachmentService.download({ attachmentId: image.id });
              }),
              map(response => {
                const blob = response.body;
                const url = URL.createObjectURL(blob);
                const safeUrl = this.sanitizer.bypassSecurityTrustUrl(url);
                return { ...image, data: safeUrl, filename: image.fileName };
              }),
              // fall back if error
              catchError(() =>
                this.attachmentService.download({ attachmentId: image.id }).pipe(
                  map(response => {
                    const blob = response.body;
                    const url = URL.createObjectURL(blob);
                    const safeUrl = this.sanitizer.bypassSecurityTrustUrl(url);
                    return { ...image, data: safeUrl, filename: image.fileName };
                  })
                )
              )
            )
          )
        );
      })
    ),
    this.attachmentStore.isLoading$,
    this.store.pipe(select(companySelector)),
    this.store.pipe(select(currentEmployeeSelector)).pipe(
      tap(employee => {
        this.currentUserId = employee.userId;
      })
    ),
    this.searchRequest$.pipe(
      filter(request => request.companyId !== null),
      switchMap((request) => {
        this.loading = true;
        return this.deviceService.getDevicesWithoutInstallationByCompany(request)
      }),
      tap(() => {
        this.loading = false; // Stop loading when data is fetched
      }),
      map((x) => {

        const sortedData = x.data.sort((a, b) => {
          // First, compare by manufacturer.name
          const manufacturerA = a.manufacturer?.name || '';
          const manufacturerB = b.manufacturer?.name || '';
        
          // Sort empty strings (null/undefined values) last
          if (manufacturerA === '' && manufacturerB === '') {
            return 0;
          }
          if (manufacturerA === '') {
            return 1;
          }
          if (manufacturerB === '') {
            return -1;
          }
        
          // Compare manufacturer names
          const manufacturerComparison = manufacturerA.localeCompare(manufacturerB);
          if (manufacturerComparison !== 0) {
            return manufacturerComparison;
          }
        
          // If manufacturer names are equal, compare by serialNumber
          const serialA = a.serialNumber?.toString() || '';
          const serialB = b.serialNumber?.toString() || '';
        
          // Sort empty strings (null/undefined values) last
          if (serialA === '' && serialB === '') {
            return 0;
          }
          if (serialA === '') {
            return 1;
          }
          if (serialB === '') {
            return -1;
          }
        
          // Compare serial numbers when neither is null or empty
          return serialA.localeCompare(serialB);
        });
        // Return the updated grid data, considering the added and removed devices
        const gridData: GridDataResult = {
          data: sortedData,
          total: x.totalCount // - addedIds.size + removedDevices.size // Adjust total count
        };
      
        return gridData;
      }),
      startWith({ data: [], total: 0 })
      
    ),
    this.searchRequest$.pipe(startWith(null)),
    this.pageChangeSub$.pipe(
      withLatestFrom(this.searchRequest$),
        map(([pageChangeEvent, searchRequest]) => ({
          pageChangeEvent,
          searchRequest,
      })),
      tap(data => {
        const pageNumber = Math.round(
          (data.pageChangeEvent.skip + data.pageChangeEvent.take) /
          data.pageChangeEvent.take
        );
        this.gridState$.next({ skip: data.pageChangeEvent.skip, pageSize: data.pageChangeEvent.take});
        this.searchRequest$.next({
          ...data.searchRequest,
          pageNumber: pageNumber,
          pageSize: data.pageChangeEvent.take,
        });
      }),
      startWith({skip: 0, take: 10})
    )
  ]).pipe(
    map(([
      gridState,
      installation,
      images,
      isUploading,
      companies,
      currentEmployee,
      availableDevicesForInstallation,
      searchRequest,
      pageChangeSub
    ]) => ({
      gridState,
      installation,
      images,
      isUploading,
      companies,
      currentEmployee,
      availableDevicesForInstallation,
      searchRequest,
      pageChangeSub
    }))
  );

  ngOnInit(): void {
    // Initialize the form group here
    this.installationForm = this.fb.group({
      type: new UntypedFormControl(),
      companyId: new UntypedFormControl(null, {
        validators: Validators.required,
        updateOn: 'change'
      }),
      name: new UntypedFormControl(null, {
        validators: Validators.required
      }),
      picture: new UntypedFormControl(),
      devices: this.fb.array([])
    });
    
    this.checkAdminPermission();
    
    this.route.paramMap.subscribe(params => {
      this.installationId = params.get('id');
    });

    if (this.installationId == "new") {
      this.isNew = true;
    } else {
      if (this.installationId) {
        this.installationStore.getInstallationById(this.installationId);
        this.attachmentStore.getAttachmentsByInstallation(this.installationId);
      }
    }

    this.isAdmin && this.store.dispatch(CompanyActions.getCompanies());
    this.store.dispatch(EmployeeActions.getCurrentEmployee());
  }

  checkAdminPermission(): void {
    const permissions = this.permissionServie.getPermissions();
    
    if (permissions && permissions.includes('Permissions.Companies.SearchGlobal')) {
      this.isAdmin = true;
    } else {
      this.isAdmin = false;
    }
  }

  private patchFormValues(installation: IGetInstallationDetailResponse): void {
    this.installationForm.patchValue({
      companyId: installation?.companyId,
      name: installation?.name
    });

    // Clear existing notes FormArray
    this.devices.clear();

    // Populate devices FormArray with the devices from the installation
    installation?.devices?.forEach(device => {
        this.devices.push(this.fb.group({
            id: device.id,
            serialNumber: device.serialNumber ?? '-',
            tagNumber: device.tagNumber ?? '-',
            type: device.type ?? '-',
            manufacturerName: device.manufacturerName,
            deviceLocationName: device.deviceLocationName
        }));
    });
  }

  get devices(): FormArray {
    return this.installationForm.get('devices') as FormArray;
  }

  addDevice(deviceId, serialNumber, tagNumber, type, manufacturerName, deviceLocationName): void {
    // Create a new FormGroup with device details
    const deviceFormGroup = this.fb.group({
      id: [deviceId],
      serialNumber: [serialNumber],
      tagNumber: [tagNumber],
      type: [type],
      manufacturerName: [manufacturerName],
      locationName: [deviceLocationName]
    });

    // Push the new FormGroup into the FormArray
    this.devices.push(deviceFormGroup);

    const searchRequest = this.searchRequest$.value;
    searchRequest.linkedDeviceIds = this.devices?.value?.map(x => x.id);

    this.searchRequest$.next(searchRequest);

    this.installationForm.markAsDirty();
  }


  editDevice(index: number): void {
    this.devices[index].get('deviceId').enable();
  }

  removeDevice(id: string, index: number): void {
    this.devices.removeAt(index);
    const searchRequest = this.searchRequest$.value;
    searchRequest.linkedDeviceIds = this.devices?.value?.map(x => x.id);
    this.searchRequest$.next(searchRequest);

    this.installationForm.markAsDirty();
  }

  saveChanges() {
    const formValues = this.installationForm.value;

    if (!this.isNew) {
      const updateRequest: IUpdateInstallationRequest = {
        id: this.installationId,
        companyId: formValues.companyId,
        name: formValues.name,
        deviceIds: formValues.devices
      };

      this.store.dispatch(InstallationActions.updateInstallation({ installation: updateRequest }));
    } else {
      const createRequest: ICreateInstallationRequest = {
        companyId: formValues.companyId,
        name: formValues.name,
        devices: formValues.devices
      };

      this.store.dispatch(InstallationActions.createInstallation({ installation: createRequest }));
    }
  }

  onCompanyChange(companyId: string): void {
    // Clear the devices array
    this.devices.clear();
  
    if (companyId) {
      this.currentCompanyId = companyId;
  
      // Update the search request to trigger device loading
      this.searchRequest$.next({
        ...this.searchRequest$.getValue(),
        companyId: companyId,
        pageNumber: 0,
        linkedDeviceIds: []
      });
  
      // Enable the devices form array
      this.installationForm.get("devices")?.enable();
    } else {
      this.installationForm.get("devices")?.disable();
    }
  
    // Optional: mark form as dirty if needed
    this.installationForm.markAsDirty();
  
    // Trigger change detection if needed
    this.cdr.detectChanges();
  }

  linkDevice(id, serialNumber, tagNumber, type, manufacturerName, deviceLocationName) {
    this.addDevice(id, serialNumber, tagNumber, type, manufacturerName, deviceLocationName);
    this.deviceStore.removeDeviceByIdForInstallation(id);
  }

  delete() {
    if(!this.isNew) {
      this.store.dispatch(InstallationActions.deleteInstallation({ id: this.installationId }))
    }
  }

  public async uploadAttachment(event: UploadEvent): Promise<void> {
    event.preventDefault();

    // Loop through the files and upload each one
    for (const file of event.files) {
      // Resize image
      const processedBlob = await resizeImageTo1920(file.rawFile);
      const processedFile = new File([processedBlob], file.name, { type: file.rawFile.type });

      var formData = new FormData();

      formData.append('file', processedFile, file.name);
       
      // Append optional IDs to FormData if they are provided
      if (this.installationId) {
        formData.append('installationId', this.installationId);
      }

      formData.append('tenantId', this.currentTenant);

      this.attachmentStore.addAttachment(formData);
    }
  }

  
  public downloadAttachment(id: string) {
    this.attachmentService.downloadMidsize({attachmentId: id}).subscribe(response => downloadBlob(response));
  }

  public deleteAttachment(id: string) {
    this.attachmentStore.removeAttachmentById(id);
  }

  // delete confirmation dialog
  public deletionDialogClose(status: string): void {
    this.deletionDialogOpened = false;

    if(status == 'delete') {
      this.delete();
    }
  }

  public deletionDialogOpen(): void {
    this.deletionDialogOpened = true;
  }

  // function on form submit
  onSubmit(request: IBaseGetAvailableDevicesForInstallationRequest) {
    const formValues = this.filterForm.value;
  
    if (!formValues.keyword || formValues.keyword.trim() === '') {
      this.resetFilter();
    } else {
      const searchRequest: IBaseGetAvailableDevicesForInstallationRequest = {
        ...request,
        pageNumber: 0,
        companyId: this.currentCompanyId,
        keyword: formValues.keyword.trim(),
      };

      this.searchRequest$.next(searchRequest);
      this.gridState$.next({ pageSize: request.pageSize, skip: 0 });
      this.updateUrlWithQuery(this.filterForm.value);
  
      this.showReset = true;
    }
  }
  
  
    // update url according to form value changes
    updateUrlWithQuery(formValues){
      // Update URL with the filter parameters
      const queryParams = {
        keyword: formValues.keyword || null,
      };
  
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: queryParams,
        queryParamsHandling: 'merge',
      });
    }
    
    // reset filter preset
    resetFilter(): void {
      // reset form
      this.filterForm.reset();
      
      this.searchRequest$.next({pageNumber: 0,pageSize: this.pageSizes.Small, companyId: this.currentCompanyId, installationId: this.installationId, linkedDeviceIds: this.devices?.value?.map(x => x.id)})
      this.gridState$.next({ pageSize: PageSize.Small, skip: 0,})
  
      // reset url params
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {},
        queryParamsHandling: '',
      });

      this.showReset = false;
    }
}